import { ReactNode, useCallback, useMemo, useState } from 'react';
import { forceCheck } from 'react-lazyload';
import classNames from 'classnames';
import { throttle } from 'lodash';

import { DraggablePanel, Flex, ResizableFlexColumn } from '@hcs/design-system';
import { Skeleton } from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import {
  DEFAULT_LAYERS,
  HcMapProps,
  PropertyMarkerTypes,
  useHcMap,
} from '@hcs/maps';
import {
  CustomCellData,
  PropertyStateCardPropsCreator,
} from '@hcs/property-state';
import {
  ControlPosition,
  PropertySpatialSearchListQueryVariables,
  PropertySpatialSearchMapQueryVariables,
  PropertyStateCerberusInput,
  PropertyStateType,
} from '@hcs/types';

import {
  PropertySpatialSearchList,
  PropertySpatialSearchListProps,
} from '../PropertySpatialSearchList';
import { PropertySpatialSearchMap } from '../PropertySpatialSearchMap';

import styles from './PropertySpatialSearch.module.css';

const CARD_WIDTH = 250;
const CARD_GAP = 15;

export interface PropertySpatialSearchProps<
  D extends CustomCellData = undefined,
> {
  propertyStateType?: PropertyStateType;
  className?: string;
  children?: ReactNode;
  childrenFirst?: boolean;
  propertyStateCardProps?: PropertyStateCardPropsCreator;
  listingStatusMarker?: boolean;
  markerType?: PropertyMarkerTypes;
  spatialSearchVariables:
    | (Omit<PropertySpatialSearchMapQueryVariables, 'limit'> &
        Omit<PropertySpatialSearchListQueryVariables, 'limit'> & {
          limit?: number;
        })
    | undefined
    | null;
  mapProps: Omit<
    HcMapProps,
    | 'dataHcName'
    | 'propertyStateCardProps'
    | 'spatialSearchVariables'
    | 'isRental'
  >;
  listProps: Omit<
    PropertySpatialSearchListProps<D>,
    'spatialSearchVariables' | 'propertyStateCardProps'
  >;
  isRental?: boolean;
  // content to render in place of the list
  listContent?: ReactNode;
  includeUpsell?: boolean;
  onChangeZipcodeResults?: PropertySpatialSearchListProps['onChangeZipcodeResults'];
  onChangeMsaResults?: PropertySpatialSearchListProps['onChangeMsaResults'];
}

const LAYERS_CONTROL: HcMapProps['layersControl'] = {
  vectilesMetricGroups: DEFAULT_LAYERS,
  position: ControlPosition.BottomRight,
};
const ZOOM_CONTROL: HcMapProps['zoomControl'] = {
  position: ControlPosition.TopRight,
};
const SATELLITE_CONTROL: HcMapProps['satelliteControl'] = {
  position: ControlPosition.BottomLeft,
};
const LIST_PANEL_MOBILE_INITIAL_Y = -80;
const LIST_PANEL_MOBILE_MIN_Y = -window.innerHeight + 100;

const dataHcName = 'property-spatial-search';

export const PropertySpatialSearch = <D extends CustomCellData = undefined>({
  className,
  mapProps,
  children,
  childrenFirst,
  isRental,
  listProps,
  propertyStateCardProps,
  listingStatusMarker,
  markerType,
  spatialSearchVariables: spatialSearchVariablesProp,
  listContent: listContentProp,
  onChangeZipcodeResults,
  onChangeMsaResults,
  propertyStateType = 'Preview',
  includeUpsell,
}: PropertySpatialSearchProps<D>) => {
  const { mapState } = useHcMap(mapProps.mapId);
  const { boundsInfo } = mapState || {};
  const { isPhoneWidth } = useScreenSize();
  const [isListPanelMobileCollapsed, setIsListPanelMobileCollapsed] =
    useState(isPhoneWidth);
  const throttledLazyRenderCheck = throttle(forceCheck, 100, {
    leading: true,
    trailing: true,
  });
  const spatialSearchVariablesMap:
    | PropertySpatialSearchListQueryVariables
    | undefined
    | null = useMemo(
    () =>
      spatialSearchVariablesProp && {
        limit: 25,
        ...spatialSearchVariablesProp,
      },
    [spatialSearchVariablesProp],
  );
  const spatialSearchVariablesList:
    | PropertySpatialSearchListQueryVariables
    | undefined
    | null = useMemo(
    () =>
      spatialSearchVariablesMap && {
        ...spatialSearchVariablesMap,
        id: {
          ...spatialSearchVariablesMap?.id,
          bbox: boundsInfo
            ? {
                swLng: boundsInfo.southWest.lng,
                swLat: boundsInfo.southWest.lat,
                neLng: boundsInfo.northEast.lng,
                neLat: boundsInfo.northEast.lat,
              }
            : undefined,
        },
      },
    [spatialSearchVariablesMap, boundsInfo],
  );

  const [highlightedProperty, setHighlightedProperty] = useState<
    PropertyStateCerberusInput | undefined
  >(undefined);

  const onListPanelMobileDragComplete = useCallback(
    (y: number) => {
      setIsListPanelMobileCollapsed(y >= LIST_PANEL_MOBILE_INITIAL_Y);
      throttledLazyRenderCheck();
    },
    [throttledLazyRenderCheck],
  );

  const mapContent = spatialSearchVariablesMap ? (
    <PropertySpatialSearchMap
      includeUpsell={includeUpsell}
      propertyStateType={propertyStateType}
      highlightedProperty={highlightedProperty}
      markerType={markerType}
      listingStatusMarker={listingStatusMarker}
      isRental={isRental}
      layersControl={LAYERS_CONTROL}
      zoomControl={ZOOM_CONTROL}
      satelliteControl={SATELLITE_CONTROL}
      {...mapProps}
      propertyStateCardProps={propertyStateCardProps}
      spatialSearchVariables={spatialSearchVariablesMap}
      showPopupOnMarkerClick
    />
  ) : (
    <Skeleton dataHcName={`${dataHcName}-loader`} />
  );

  const listContent = listContentProp
    ? listContentProp
    : spatialSearchVariablesList &&
      boundsInfo && (
        <PropertySpatialSearchList
          {...listProps}
          includeUpsell={includeUpsell}
          theme={isPhoneWidth ? { Header: styles.ListHeaderMobile } : undefined}
          propertyStateType={propertyStateType}
          propertyStateCardProps={propertyStateCardProps}
          onPropertyEnter={(propertyStateCerberusInput) => {
            setHighlightedProperty(propertyStateCerberusInput);
            listProps.onPropertyEnter?.(propertyStateCerberusInput);
          }}
          onPropertyLeave={() => {
            setHighlightedProperty(undefined);
            listProps.onPropertyLeave?.();
          }}
          spatialSearchVariables={spatialSearchVariablesList}
          onChangeZipcodeResults={onChangeZipcodeResults}
          onChangeMsaResults={onChangeMsaResults}
          isShowingSortControl={!isListPanelMobileCollapsed}
        />
      );

  if (isPhoneWidth) {
    return (
      <Flex fullHeight fullWidth direction="column" gap="0px">
        {mapContent}
        <div className={styles.MobileListSpacer}></div>
        <DraggablePanel
          active
          className={styles.ListPanelMobile}
          dataHcName={'platform-search-mobile-list-panel'}
          initialY={LIST_PANEL_MOBILE_INITIAL_Y}
          minY={LIST_PANEL_MOBILE_MIN_Y}
          snap
          snapY={[LIST_PANEL_MOBILE_INITIAL_Y, LIST_PANEL_MOBILE_MIN_Y]}
          onDrag={throttledLazyRenderCheck}
          onDragComplete={onListPanelMobileDragComplete}
        >
          {listContent}
        </DraggablePanel>
      </Flex>
    );
  }
  return (
    <div className={classNames(styles.PageContent, className)}>
      {childrenFirst && children}
      <div className={styles.MapContainer}>{mapContent}</div>
      <ResizableFlexColumn
        dataHcName={`${dataHcName}-results-pane-resize`}
        classNameInner={styles.ResultsPaneInner}
        columns={{
          width: CARD_WIDTH,
          gap: CARD_GAP,
          snapTo: true,
          adjustment: 20,
        }}
        width={CARD_WIDTH * 2}
      >
        {listContent}
      </ResizableFlexColumn>
      {!childrenFirst && children}
    </div>
  );
};
